import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface Hit {
  id: string;
  label: string | null;
  score: number;
}

interface Results {
  accounts: Hit[];
  contacts: Hit[];
  opportunities: Hit[];
  leads: Hit[];
}

type EntityGroup = {
  key: string;
  label: string;
  hits: Hit[];
  linkPrefix: string;
};

export function SearchPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Results | null>(null);

  async function run() {
    if (q.trim().length < 2) return;
    const { data } = await apiClient.get<Results>('/analytics/search', {
      params: { q },
    });
    setResults(data);
  }

  const groups: EntityGroup[] = results
    ? [
        { key: 'accounts', label: t('nav.accounts'), hits: results.accounts, linkPrefix: '/accounts/' },
        { key: 'contacts', label: t('nav.contacts'), hits: results.contacts, linkPrefix: '/accounts/' },
        { key: 'opportunities', label: t('nav.opportunities'), hits: results.opportunities, linkPrefix: '/opportunities/' },
        { key: 'leads', label: t('nav.leads'), hits: results.leads, linkPrefix: '/leads/' },
      ]
    : [];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t('nav.search')}</h1>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
      >
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search accounts, contacts, opportunities..."
          className="max-w-md"
        />
        <Button type="submit">{t('nav.search')}</Button>
      </form>
      {groups.map((group) => (
        <div key={group.key} className="rounded-lg border bg-card p-4">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {group.label} ({group.hits.length})
          </h2>
          <ul className="space-y-1">
            {group.hits.map((hit) => (
              <li key={hit.id}>
                <Link
                  to={`${group.linkPrefix}${hit.id}`}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {hit.label ?? '—'}
                </Link>
              </li>
            ))}
            {group.hits.length === 0 && (
              <li className="text-sm text-muted-foreground">—</li>
            )}
          </ul>
        </div>
      ))}
    </div>
  );
}
